// ============================================================
// ANSICHTSSACHE-AGENT – Gruppierte Listen: Karten auf dem Grund
// ============================================================
// Seit dem 24.09.2026 (Entwurf in Figma, Rahmen „Projekte —
// Entwurf: Ansichtsgruppen als Karten") liegt jede Gruppe einer
// gruppierbaren Liste als eigene KARTE auf dem grauen Grund, und
// ungruppiert steht dieselbe Liste als EINE Karte ohne Kopf dort.
// Gebaut wurde das zuerst fuer die Projekte; die Personenliste
// bekam am selben Tag Gruppenzeilen in einer durchgehenden
// Tabelle — und der Nutzer sah zwei Listen, die dasselbe tun und
// verschieden aussehen. Seither stehen die Bauteile HIER und sind
// die einzige Fassung davon.
//
//   Rasterkopf       Spaltenkoepfe auf dem Grund, klebt oben
//   Kartenstapel     die Karten untereinander, 12px vom Rand
//   Ansichtskarte    eine Gruppe: Kopf zum Zuklappen, darunter
//                    ihre Zeilen
//   KopfloseKarte    die Liste ohne Gruppierung
//
// WARUM KEIN <table>: jede Karte ist ein eigener Kasten, und eine
// Tabelle je Karte bemaesse ihre Spalten nach dem eigenen Inhalt
// — die Spaltenkante sprange von Karte zu Karte. Deshalb ein
// CSS-Raster (`Raster`), das Kopf, Karten und Zeilen teilen, mit
// Breiten in `fr` oder fest, nie `auto`.
//
// Die Zeile selbst baut jede Liste; sie legt nur `RASTERZEILE`
// und `ZEILENEINZUG` an und traegt `relative`, damit ein
// `Zeilenverweis` darin die ganze Zeile anklickbar macht.
// ============================================================

import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/** Das gemeinsame Spaltenmass von Kopf, Karten und Zeilen. */
export type Raster = {
  /** `gridTemplateColumns` — als Stil, weil zur Laufzeit gebaut. */
  stil: React.CSSProperties;
  /** Feste Klassen des Rasters (`grid items-center gap-x-4 …`). */
  klasse: string;
  /** Wie weit der Titel einer Karte reicht — ueber die Textspalten. */
  titel: React.CSSProperties;
};

/**
 * Eine Textspalte, die auf dem Telefon nicht verschwindet.
 *
 * `minmax(0, 2fr)` gibt nach, bis nichts mehr bleibt — und auf dem
 * iPhone blieb nichts: die festen Spalten rechts (Status, Budget,
 * Aufwand) sind zusammen breiter als das Bild, und der Name des
 * Projekts stand auf null Pixeln (10.10.2026). Die Untergrenze
 * kommt deshalb aus `--rastertext` (`styles/globals.css`): auf
 * breiten Schirmen 0 wie bisher, unter `md` so breit, dass ein
 * Name lesbar bleibt. Was dann nicht mehr ins Bild passt, rollt
 * seitlich (`Rasterkopf`, `Kartenstapel`).
 */
export function textspur(spur: string): string {
  return spur.replace(/^minmax\(\s*0\s*,/, "minmax(var(--rastertext),");
}

/**
 * Ein Raster aus Spaltenbreiten. `titelSpalten` sagt, ueber wie
 * viele Spalten der Kopf einer Karte seinen Titel legt; was
 * rechts davon bleibt, traegt Summen genau ueber ihrer Spalte.
 */
/**
 * Auf dem Telefon bleibt beim seitlichen Wischen das erste Stueck
 * jeder Zeile stehen — der Name, wie in den Tabellenlisten der Titel
 * (`styles/globals.css`, Listentabelle). Sein Grund kommt aus
 * `--rastergrund`, das Karte, Kartenkopf und Spaltenkopf je selbst
 * setzen; gestreckt, damit darunter nichts durchscheint. Die Flaeche
 * des Zeilenverweises reicht dann nur ueber den Namen — den Tipp
 * daneben loest `ZeilenTippen` aus (`zeilen-tippen.tsx`).
 */
const NAME_STEHT = cn(
  "max-md:[&>:first-child]:sticky max-md:[&>:first-child]:left-0 max-md:[&>:first-child]:z-[2]",
  "max-md:[&>:first-child]:flex max-md:[&>:first-child]:flex-col max-md:[&>:first-child]:justify-center max-md:[&>:first-child]:self-stretch",
  "max-md:[&>:first-child]:bg-[var(--rastergrund)] max-md:[&>:first-child]:shadow-[inset_-1px_0_0_var(--color-border-subtle)]",
);

export function rasterAus(
  spalten: string[],
  titelSpalten = spalten.length,
  klasse = cn("grid items-center gap-x-4", NAME_STEHT),
): Raster {
  return {
    stil: { gridTemplateColumns: spalten.map(textspur).join(" ") },
    klasse,
    titel: { gridColumn: `span ${titelSpalten} / span ${titelSpalten}` },
  };
}

/** Die Karte selbst. */
//
// `overflow-clip` statt `hidden` auf dem Telefon: `hidden` machte die
// Karte zum Rollkasten, und ein klebender Name (`NAME_STEHT`) haelt
// sich an den naechsten Rollkasten — er klebte an der Karte, die
// selbst nicht rollt, statt an der Liste.
export const LISTENKARTE =
  "bg-bg-primary border-border overflow-hidden rounded-xl border max-md:overflow-clip [--rastergrund:var(--color-bg-primary)]";

/**
 * Die Kante, an der Spaltenkopf und Karten beginnen: 12px Abstand
 * zum Rand des Blatts, dazu 1px, so breit wie der Kartenrand. Der
 * Spaltenkopf liegt auf dem Grund und hat keinen Rand — ohne das
 * durchsichtige Pixel stuende jede Ueberschrift um eins daneben.
 */
export const KARTENRAND = "border-x border-transparent";

/** Der Einzug einer Zeile: links die Steuerspalte, rechts 16px. */
export const ZEILENEINZUG = "pl-gutter pr-4";

/** Eine Zeile in einer Karte — Hoehe, Ueberfahren, Fokus. */
export const RASTERZEILE = cn(
  "duration-fast hover:bg-bg-secondary relative py-2.5 transition-colors",
  "focus-visible:bg-bg-secondary focus-visible:outline-none",
);

/** Der Verweis im Kopf einer Karte — „Zur Firma“, „Rechnung aus Zeiten“. */
export const KOPFAKTION =
  "text-caption text-accent hover:text-accent-hover shrink-0 font-medium whitespace-nowrap underline-offset-2 hover:underline";

/** Die Spaltenkoepfe — auf dem Grund, sie gehoeren zu allen Karten. */
export function Rasterkopf({ raster, children }: { raster: Raster; children: React.ReactNode }) {
  // `min-w-min` auf dem Telefon: so breit wie die Untergrenzen der
  // Spalten (`textspur`) — passt das nicht ins Bild, rollt die
  // Liste seitlich, statt die Textspalten zu tilgen. Spaltenkopf
  // und Karten rechnen mit demselben Raster und werden gleich breit.
  return (
    <div className="bg-bg-secondary sticky top-0 z-10 px-3 [--rastergrund:var(--color-bg-secondary)] max-md:min-w-min">
      <div
        style={raster.stil}
        className={cn(
          raster.klasse,
          KARTENRAND,
          ZEILENEINZUG,
          "text-overline text-text-tertiary pt-3 pb-2",
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** Die Karten untereinander — derselbe Abstand wie beim Spaltenkopf. */
export function Kartenstapel({ children }: { children: React.ReactNode }) {
  // `min-w-min` wie beim `Rasterkopf`.
  // `data-kartenstapel`: hier haengt `ZeilenTippen` seinen Horcher an.
  return (
    <div data-kartenstapel="" className="space-y-3 px-3 pb-3 max-md:min-w-min">
      {children}
    </div>
  );
}

/**
 * Die Karte einer Gruppe.
 *
 * Sie laesst sich zuklappen, ueber `<details>`, also ohne eine
 * Zeile JavaScript; zugeklappt bleibt der Kopf stehen.
 */
export function Ansichtskarte({
  titel,
  anzahl,
  hinweis,
  aktion,
  zu,
  raster,
  rechts,
  children,
}: {
  titel: React.ReactNode;
  anzahl: number;
  /** Woran die Gruppe haengt — eine Zeile unter dem Namen. */
  hinweis?: React.ReactNode;
  /** Was man mit der ganzen Gruppe tut — ein Verweis mit `KOPFAKTION`. */
  aktion?: React.ReactNode;
  /** Zugeklappt starten. */
  zu?: boolean;
  raster: Raster;
  /** Summen rechts neben dem Titel, je in ihrer Spalte. */
  rechts?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className={LISTENKARTE}>
      <details open={!zu} className="group/fach">
        {/* Der Kopf liegt im GLEICHEN Raster wie die Zeilen
            darunter: so steht eine Summe genau ueber der Spalte, die
            sie summiert. Das Klapp-Zeichen haengt im linken Rand
            (absolut) statt in der Zeile — sonst schoebe es den
            Namen um seine Breite nach rechts. Es steht auf der
            Mitte der ERSTEN Zeile, nicht des ganzen Kopfes: es
            gehoert zum Namen, nicht zum Hinweis darunter.
            Der Schatten liegt nur an, solange die Karte offen ist —
            zugeklappt steht nichts darunter, worauf er fallen
            koennte. `z-[1]`, damit die erste Zeile ihn nicht
            ueberdeckt. */}
        <summary
          style={raster.stil}
          className={cn(
            raster.klasse,
            ZEILENEINZUG,
            "bg-bg-head relative z-[1] cursor-pointer list-none py-2.5 [--rastergrund:var(--color-bg-head)]",
            "duration-fast hover:bg-bg-secondary group-open/fach:shadow-e2 transition-colors",
            "[&::-webkit-details-marker]:hidden",
          )}
        >
          {/* Auf dem Telefon reicht der Titel bis in die Steuerspalte
              (`-ml`/`pl`): klebt er beim Wischen (`NAME_STEHT`), ist er
              der Bezugskasten des Klapp-Zeichens — das stuende sonst
              mitten in seinem Text statt davor. */}
          <span
            style={raster.titel}
            className="max-md:-ml-gutter max-md:pl-gutter flex min-w-0 flex-col justify-center"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="w-gutter absolute top-2.5 left-0 grid h-[19px] place-items-center">
                <ChevronRight
                  className="text-text-tertiary duration-fast ease-standard h-3.5 w-3.5 transition-transform group-open/fach:rotate-90"
                  aria-hidden
                />
              </span>
              {/* Der Name der Gruppe kuerzt NICHT: „Eingrei…“ ist
                  keine Ueberschrift. Was nachgibt, ist der Hinweis
                  darunter — er erklaert nur. */}
              <span className="text-body-sm text-text-primary shrink-0 font-semibold whitespace-nowrap">
                {titel}
              </span>
              <span className="numeric text-caption text-text-tertiary shrink-0">{anzahl}</span>
            </span>

            {/* Woran die Gruppe haengt, und was man mit ihr tut —
                beides unter dem Namen und nicht rechts bei den
                Summen: dort stehen Zahlen, die sich auf die Spalte
                darueber beziehen. */}
            {(hinweis || aktion) && (
              <span className="mt-0.5 flex min-w-0 items-center gap-2">
                {hinweis && (
                  <span className="text-caption text-text-tertiary truncate">{hinweis}</span>
                )}
                {aktion}
              </span>
            )}
          </span>

          {rechts}
        </summary>

        {children}
      </details>
    </section>
  );
}

/** Eine Karte ohne Kopf — die Liste ohne Gruppierung. */
export function KopfloseKarte({ children }: { children: React.ReactNode }) {
  return <section className={LISTENKARTE}>{children}</section>;
}
